import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import { AttendeeCandidatesSelect } from 'src/components/domain/attendees/attendee-candidates-select';
import { CompanyContactsSelect } from 'src/components/domain/contacts/company-contacts-select';
import { CompanyUsersSelect } from 'src/components/domain/users/components/company-users-select';
import { LggOptionsDropdownButtonWithCustomTrigger } from 'src/components/general/button/dropdown-button';
import {
  DrawerOption,
  OptionsBottomDrawer,
} from 'src/components/general/drawer/bottom/options-bottom-drawer';
import { useShowConfirmationModal } from 'src/components/general/feedback/hooks/use-show-confirmation-modal';
import { Icon } from 'src/components/general/icon';
import { FlexRow } from 'src/components/layout/flex-row';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useVisible } from 'src/hooks/use-visible';
import { AddAssigneeIcon } from '../../display/assignee-label';
import { Chip } from '../../display/chip';
import { LggDateTimePicker } from '../../inputs/custom-date-pickers/lgg-date-time-picker';
import { inlineFormInputStyles } from '../../inputs/inline-text-inputs';
import { SelectOption } from '../../inputs/select/select';
import { AdaptiveOptionItem } from '../../items/adaptive-option-item';
import { inlineFormSelectStyles } from './inline-form-select';

export const InlineFormDateTimePicker = styled(LggDateTimePicker)`
  ${up('md')} {
    height: 32px;
  }

  input {
    ${inlineFormInputStyles};
  }

  .lgg-date-time-picker-input {
    .ant-picker {
      ${inlineFormInputStyles};
      height: 30px;
    }
  }

  .ant-dropdown {
    min-width: max-content !important;
  }
`;

export const ExtraOptionIcon = styled(Icon)`
  height: 16px;
  width: 16px;

  svg {
    height: 16px;
    width: 16px;

    path {
      fill: ${({ theme }) => theme.colors.flint};
    }
  }
`;

const AddExtraFieldIcon = styled(Icon)`
  height: 9px;
  margin-right: 13px;
  width: 9px;

  svg {
    height: 9px;
    width: 9px;

    path {
      fill: ${({ theme }) => theme.colors.gogo};
    }
  }
`;

const AddExtraFieldOption = styled(FlexRow)`
  align-items: center;
  color: ${({ theme }) => theme.colors.gogo};
  cursor: pointer;
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 12px;
  line-height: 15px;
  margin-top: 20px;
  padding: 0 3.5px;
  width: max-content;
`;

type AddExtraFieldButtonProps = {
  options: DrawerOption[];
};

export const AddExtraFieldButton = ({ options }: AddExtraFieldButtonProps) => {
  const visibilityHandler = useVisible();
  const breakpointUpMd = useBreakpoint(up('md'));
  const hasOptions = options.length > 0;
  const { t } = useTranslation(['common']);

  const trigger = useMemo(() => {
    return (
      <AddExtraFieldOption
        data-lgg-id="add-contact-field-extra-field-trigger"
        onClick={hasOptions ? visibilityHandler.show : undefined}
      >
        <AddExtraFieldIcon type="plus" />
        <span>{t('common:addField')}</span>
      </AddExtraFieldOption>
    );
  }, [hasOptions, visibilityHandler.show, t]);

  if (!hasOptions) {
    return trigger;
  }

  return breakpointUpMd ? (
    <LggOptionsDropdownButtonWithCustomTrigger
      visibilityHandler={visibilityHandler}
      customTrigger={trigger}
      options={options}
    />
  ) : (
    <>
      {trigger}
      <OptionsBottomDrawer
        title={t('common:addMoreFields')}
        onClose={visibilityHandler.close}
        visible={visibilityHandler.visible}
        options={options}
      />
    </>
  );
};

export const useShowCloseConfirmationModal = () => {
  const { t } = useTranslation(['common']);
  const showConfirmationModal = useShowConfirmationModal();

  const showCloseConfirmationModal = useCallback(
    ({ onCancel }: { onCancel?: () => void }) => {
      showConfirmationModal({
        title: t('common:unsavedChangesModal.title'),
        message: t('common:unsavedChangesModal.message'),
        cancelButtonText: t('common:unsavedChangesModal.cancelButtonText'),
        confirmButtonText: t('common:unsavedChangesModal.confirmButtonText'),
        confirmButtonType: 'primary',
        testId: 'close-without-saving-modal',
        onCancel,
        // Empty becasue we just need to dismiss the modal
        onConfirm: () => {},
      });
    },
    [showConfirmationModal, t],
  );

  return showCloseConfirmationModal;
};

export const InlineFormUserSelect = styled(CompanyUsersSelect)`
  ${inlineFormSelectStyles}
` as typeof CompanyUsersSelect;

export const InlineFormContactSelect = styled(CompanyContactsSelect)`
  ${inlineFormSelectStyles}
` as typeof CompanyContactsSelect;

export const AttendeesSelect = styled(AttendeeCandidatesSelect)`
  ${inlineFormSelectStyles}
` as typeof AttendeeCandidatesSelect;

const StyledAddAssigneeIcon = styled(AddAssigneeIcon)`
  .rounded-dashed-container {
    padding: 1px;
  }

  .ant-avatar {
    height: 18px;
    width: 18px;

    svg {
      width: 10px;
      height: 10px;
    }
  }
`;

type UserSelectValueProps = {
  value: SelectOption<number>;
};

const StyledAssigneeName = styled.span`
  color: ${({ theme }) => theme.colors.smalt};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 13px;
  line-height: 16px;
`;

const StyledChip = styled(Chip)`
  width: 22px;
  height: 22px;
  margin-right: 0;
`;

const UserSelectContentContainer = styled(FlexRow)`
  align-items: center;
  gap: 10px;
`;

export const UserSelectValue = ({ value, ...rest }: UserSelectValueProps) => {
  const chipData = value.chip;

  return (
    <UserSelectContentContainer {...rest}>
      {chipData ? <StyledChip size="medium" {...chipData} /> : null}
      <StyledAssigneeName className="value-label">{value?.label}</StyledAssigneeName>
    </UserSelectContentContainer>
  );
};

export const UserSelectPlaceholder = ({
  placeholder,
  ...rest
}: {
  placeholder: string;
}) => {
  return (
    <UserSelectContentContainer {...rest}>
      <StyledAddAssigneeIcon onClick={() => {}} showPlusIcon={false} />
      <span className="placeholder-label">{placeholder}</span>
    </UserSelectContentContainer>
  );
};

export const InlineFormDeleteOption = styled(AdaptiveOptionItem)`
  svg {
    path {
      fill: ${({ theme }) => theme.colors.secondaryCoral};
    }
  }

  .adaptive-option-label {
    &,
    &:hover {
      color: ${({ theme }) => theme.colors.secondaryCoral} !important;
    }

    margin-left: 20px;

    ${up('md')} {
      margin-left: 10px;
    }
  }
`;
